/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel

import io.peekandpoke.klang.audio_bridge.AdsrCurve

/**
 * Mutable sub-objects (`Svd*`) of [SprudelVoiceData], holding cohesive clusters of voice fields.
 *
 * **Why grouped:** the per-event leaf clone copies the whole [SprudelVoiceData]. A flat ~105-field class
 * copies all fields even though a leaf's data is mostly null. Grouping the optional clusters means a null
 * cluster is a single null reference (one slot, not cloned) instead of N null fields. [SprudelVoiceData.clone]
 * deep-copies only the non-null groups; setters lazily create a group on first write and then mutate it in
 * place (zero-copy), preserving the single-owner mutation model.
 *
 * These are sprudel-internal mutable mirrors. On the wire the voice doors' groups travel as the instruments' slot
 * keys (`classic()`'s `<door>.<param>`, phase 3 step 8): [SprudelVoiceData.toVoiceData] maps across.
 *
 * `copy()` (the generated data-class copy) is the per-group deep clone — every field is an immutable scalar,
 * so a shallow `copy()` fully detaches the clone. Each group has a `mergeSvd*` helper (field-wise `over`-wins,
 * fresh result) used by [SprudelVoiceData.merge]/[SprudelVoiceData.mergeFrom].
 */

/** ADSR amplitude envelope. */
data class SvdAdsr(
    var attack: Double? = null,
    var decay: Double? = null,
    var sustain: Double? = null,
    var release: Double? = null,
    var attackCurve: AdsrCurve? = null,
    var decayCurve: AdsrCurve? = null,
    var releaseCurve: AdsrCurve? = null,
    /** Whether the voice envelope shapes this voice at all (`classic()`'s `adsr.on`); `null` = unset, it stays on. */
    var on: Boolean? = null,
)

/**
 * One filter (low/high/band/notch): cutoff + resonance/Q plus an optional dynamic envelope
 * (attack/decay/sustain/release/[env] depth). The flat names differ per filter type
 * (cutoff/hcutoff/bandf/notchf, resonance/hresonance/bandq/nresonance, and the `lp`/`hp`/`bp`/`nf`
 * envelope prefixes) — they all map onto these generic fields via the accessors on [SprudelVoiceData].
 */
data class SvdFilter(
    var cutoff: Double? = null,
    var resonance: Double? = null,
    var attack: Double? = null,
    var decay: Double? = null,
    var sustain: Double? = null,
    var release: Double? = null,
    var env: Double? = null,
    /** Cascade count (C5, `lpx`/`hpx`): null = 1. Only lp/hp surfaces exist. */
    var passes: Double? = null,
    /** The envelope's stage curves (`lpfCurves`, `hpfCurves`, `bpfCurves`, `notchCurves`); null = unset. */
    var attackCurve: AdsrCurve? = null,
    var decayCurve: AdsrCurve? = null,
    var releaseCurve: AdsrCurve? = null,
)

/**
 * Pitch modulation: glide ([accelerate]) + vibrato. [vibrato] is the rate in Hz, [vibratoMod] the depth in semitones,
 * [vibratoRangeFrom] / [vibratoRangeTo] where the swing sits and [vibratoPhase] the LFO's phase in cycles
 * (`vib(rate, semitones, rangeFrom, rangeTo, phase)`); the wire carries them as `classic()`'s `vibrato.rate`,
 * `vibrato.semitones`, `vibrato.rangeFrom`, `vibrato.rangeTo` and `vibrato.phase` slots, and [accelerate] as the flat
 * `accelerate` slot.
 */
data class SvdPitchMod(
    /** Pitch glide in SEMITONES from the onset to the gate close, then held (converted from octaves, 2026-08-24). */
    var accelerate: Double? = null,
    var vibrato: Double? = null,
    var vibratoMod: Double? = null,
    var vibratoRangeFrom: Double? = null,
    var vibratoRangeTo: Double? = null,
    var vibratoPhase: Double? = null,
)

/**
 * Pitch envelope: `penv(semitones, attack, decay, sustain, release)` and `penvCurves(attack, decay, release)`.
 * [pEnv] is the `semitones` (the switch). The wire carries them as `classic()`'s `penv.*` and `penvCurves.*` slots
 * (`classicSlotParams`).
 */
data class SvdPitchEnv(
    var pAttack: Double? = null,
    var pDecay: Double? = null,
    var pSustain: Double? = null,
    var pRelease: Double? = null,
    var pEnv: Double? = null,
    var pAttackCurve: AdsrCurve? = null,
    var pDecayCurve: AdsrCurve? = null,
    var pReleaseCurve: AdsrCurve? = null,
)

/**
 * FM synthesis, `fm(depth, ratio, attack, decay, sustain, release)`: [fmEnv] is the depth in Hz, [fmh] the ratio, the
 * rest the depth envelope's stages. The wire carries them as `classic()`'s `fm.*` slots (pitch pipeline step 4).
 */
data class SvdFm(
    var fmh: Double? = null,
    var fmAttack: Double? = null,
    var fmDecay: Double? = null,
    var fmSustain: Double? = null,
    var fmEnv: Double? = null,
    var fmRelease: Double? = null,
)

/** Distortion + lo-fi (sample-rate / bit-depth reduction). */
data class SvdDistortion(
    var distort: Double? = null,
    var distortShape: String? = null,
    var coarse: Double? = null,
    var crush: Double? = null,
)

/** Phaser. */
data class SvdPhaser(
    var phaserRate: Double? = null,
    var phaserDepth: Double? = null,
    var phaserCenter: Double? = null,
    var phaserSweep: Double? = null,
    /** Minimum dry coefficient of the C4 wet/dry law (`phaserFloor`); null = engine default 1.0 (additive). */
    var phaserFloor: Double? = null,
)

/** Tremolo. */
data class SvdTremolo(
    var tremoloRate: Double? = null,
    var tremoloDepth: Double? = null,
    var tremoloShape: String? = null,
)

/** Body resonator: material + dry/wet mix + broadband dry floor. */
data class SvdBody(
    var material: String? = null,
    var mix: Double? = null,
    var floor: Double? = null,
)

/** Vowel / formant resonator: vowel + dry/wet mix + broadband dry floor. */
data class SvdVowel(
    var vowel: String? = null,
    var mix: Double? = null,
    var floor: Double? = null,
)

/** Sample playback manipulation. */
data class SvdSample(
    var begin: Double? = null,
    var end: Double? = null,
    var speed: Double? = null,
    var unit: String? = null,
    var loop: Boolean? = null,
    var cut: Int? = null,
)

// --- Field-wise merge helpers: `over` wins per field; always return a fresh, single-owner group. ----------

fun mergeSvdAdsr(base: SvdAdsr?, over: SvdAdsr?): SvdAdsr? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdAdsr(
        attack = over.attack ?: base.attack,
        decay = over.decay ?: base.decay,
        sustain = over.sustain ?: base.sustain,
        release = over.release ?: base.release,
        attackCurve = over.attackCurve ?: base.attackCurve,
        decayCurve = over.decayCurve ?: base.decayCurve,
        releaseCurve = over.releaseCurve ?: base.releaseCurve,
        on = over.on ?: base.on,
    )
}

fun mergeSvdFilter(base: SvdFilter?, over: SvdFilter?): SvdFilter? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdFilter(
        cutoff = over.cutoff ?: base.cutoff,
        resonance = over.resonance ?: base.resonance,
        attack = over.attack ?: base.attack,
        decay = over.decay ?: base.decay,
        sustain = over.sustain ?: base.sustain,
        release = over.release ?: base.release,
        env = over.env ?: base.env,
        passes = over.passes ?: base.passes,
        attackCurve = over.attackCurve ?: base.attackCurve,
        decayCurve = over.decayCurve ?: base.decayCurve,
        releaseCurve = over.releaseCurve ?: base.releaseCurve,
    )
}

fun mergeSvdPitchMod(base: SvdPitchMod?, over: SvdPitchMod?): SvdPitchMod? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdPitchMod(
        accelerate = over.accelerate ?: base.accelerate,
        vibrato = over.vibrato ?: base.vibrato,
        vibratoMod = over.vibratoMod ?: base.vibratoMod,
        vibratoRangeFrom = over.vibratoRangeFrom ?: base.vibratoRangeFrom,
        vibratoRangeTo = over.vibratoRangeTo ?: base.vibratoRangeTo,
        vibratoPhase = over.vibratoPhase ?: base.vibratoPhase,
    )
}

fun mergeSvdPitchEnv(base: SvdPitchEnv?, over: SvdPitchEnv?): SvdPitchEnv? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdPitchEnv(
        pAttack = over.pAttack ?: base.pAttack,
        pDecay = over.pDecay ?: base.pDecay,
        pSustain = over.pSustain ?: base.pSustain,
        pRelease = over.pRelease ?: base.pRelease,
        pEnv = over.pEnv ?: base.pEnv,
        pAttackCurve = over.pAttackCurve ?: base.pAttackCurve,
        pDecayCurve = over.pDecayCurve ?: base.pDecayCurve,
        pReleaseCurve = over.pReleaseCurve ?: base.pReleaseCurve,
    )
}

fun mergeSvdFm(base: SvdFm?, over: SvdFm?): SvdFm? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdFm(
        fmh = over.fmh ?: base.fmh,
        fmAttack = over.fmAttack ?: base.fmAttack,
        fmDecay = over.fmDecay ?: base.fmDecay,
        fmSustain = over.fmSustain ?: base.fmSustain,
        fmEnv = over.fmEnv ?: base.fmEnv,
        fmRelease = over.fmRelease ?: base.fmRelease,
    )
}

fun mergeSvdDistortion(base: SvdDistortion?, over: SvdDistortion?): SvdDistortion? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdDistortion(
        distort = over.distort ?: base.distort,
        distortShape = over.distortShape ?: base.distortShape,
        coarse = over.coarse ?: base.coarse,
        crush = over.crush ?: base.crush,
    )
}

fun mergeSvdPhaser(base: SvdPhaser?, over: SvdPhaser?): SvdPhaser? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdPhaser(
        phaserRate = over.phaserRate ?: base.phaserRate,
        phaserDepth = over.phaserDepth ?: base.phaserDepth,
        phaserCenter = over.phaserCenter ?: base.phaserCenter,
        phaserSweep = over.phaserSweep ?: base.phaserSweep,
        phaserFloor = over.phaserFloor ?: base.phaserFloor,
    )
}

fun mergeSvdTremolo(base: SvdTremolo?, over: SvdTremolo?): SvdTremolo? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdTremolo(
        tremoloRate = over.tremoloRate ?: base.tremoloRate,
        tremoloDepth = over.tremoloDepth ?: base.tremoloDepth,
        tremoloShape = over.tremoloShape ?: base.tremoloShape,
    )
}

fun mergeSvdSample(base: SvdSample?, over: SvdSample?): SvdSample? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdSample(
        begin = over.begin ?: base.begin,
        end = over.end ?: base.end,
        speed = over.speed ?: base.speed,
        unit = over.unit ?: base.unit,
        loop = over.loop ?: base.loop,
        cut = over.cut ?: base.cut,
    )
}

fun mergeSvdBody(base: SvdBody?, over: SvdBody?): SvdBody? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdBody(
        material = over.material ?: base.material,
        mix = over.mix ?: base.mix,
        floor = over.floor ?: base.floor,
    )
}

fun mergeSvdVowel(base: SvdVowel?, over: SvdVowel?): SvdVowel? {
    if (base == null) return over?.copy()
    if (over == null) return base.copy()
    return SvdVowel(
        vowel = over.vowel ?: base.vowel,
        mix = over.mix ?: base.mix,
        floor = over.floor ?: base.floor,
    )
}
